import './ModalRightDrawer.css';
import { Overlay } from '@ipfabric/ui-components/Overlay/Overlay';
import { useOptionallyControlledState } from '@ipfabric/ui-components/utils/react/useOptionallyControlledState';
import classNames from 'classnames';
import { AnimationClose } from 'components/AnimationClose/AnimationClose';
import { type FC, type ReactNode, useRef } from 'react';
import { Transition } from 'react-transition-group';
import type { TransitionStatus } from 'react-transition-group/Transition';
import { animations } from 'utils/constants';

type ChildrenProps = {
  /** Use this or externally controlled `closing` field to close the modal with animation */
  closeModal: () => void;
};

export interface ModalRightDrawerProps {
  /**
   * This can be used to start closing transition from outside.
   *
   * Don't forget to reset it to false after transition ends, otherwise
   * you won't be able to open the drawer again. Or pass a function as children
   * and use its `closeModal` argument.
   */
  closing?: boolean;
  setClosing?: (value: boolean) => void;

  children: ReactNode | ((props: ChildrenProps) => ReactNode);
  // useful to stretch content to full height, but can cause problems for content not styled for flex container
  flexContent?: boolean;
  noContentPadding?: boolean;
  onClose: () => void;
  size?: 'sm' | 'lg';
  title?: ReactNode;
  /** Don't apply default header styling element on title prop */
  titleUnstyled?: boolean;
}

/**
 * @example
 * <ModalRightDrawer
 *   title="Hello World"
 *   onClose={setOpen(false)}
 * >
 *   {({closeModal}) => <>
 *     <p>Hi!</p>
 *     <Button onClick={closeModal}>Close</Button>
 *   </>}
 * </ModalRightDrawer>
 */
export const ModalRightDrawer: FC<ModalRightDrawerProps> = (props) => {
  const [closing, setClosing] = useOptionallyControlledState<boolean>(
    props.closing,
    props.setClosing,
    false,
  );
  const ref = useRef<HTMLDivElement>(null);

  return (
    <Overlay
      alignment='right'
      onClose={() => {
        setClosing(true);
      }}
    >
      <Transition
        in={!closing}
        timeout={animations.FASTEST}
        nodeRef={ref}
        onExited={() => {
          props.onClose();
        }}
      >
        {(status: TransitionStatus) => (
          <div
            ref={ref}
            className={classNames(
              'modal-right-drawer',
              {
                sm: 'modal-right-drawer--sm',
                lg: 'modal-right-drawer--lg',
              }[props.size ?? 'lg'],
              'animated fastest',
              status === 'entering' || status === 'entered'
                ? 'slideInRight'
                : 'slideOutRight',
            )}
          >
            <div className='modal-right-drawer__header'>
              {props.titleUnstyled ? (
                props.title
              ) : (
                <h3 className='m-0'>{props.title}</h3>
              )}
              <AnimationClose
                className='ml-auto'
                onClick={() => {
                  setClosing(true);
                }}
              />
            </div>
            <div
              className={classNames(
                'modal-right-drawer__content',
                !props.noContentPadding && 'p-8 pt-6',
                props.flexContent && 'flex flex-col',
              )}
            >
              {typeof props.children === 'function'
                ? props.children({
                    closeModal: () => {
                      setClosing(true);
                    },
                  })
                : props.children}
            </div>
          </div>
        )}
      </Transition>
    </Overlay>
  );
};
